import { ConnectedProps } from "react-redux";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import BackButton from "../BackButton";
import TabletPageDefault, { TabletContainer } from "../Layout/TabletLayout";
import { MainColumn, RightRail, TwoColumnGrid } from "../Layout/TwoColumn";
import { connector } from "./redux";
import { showTranslated, translate } from "../../../config/translator";
import MatchStatusDisplay from "./MatchStatusDisplay";
import {
  getCardEvents,
  getChangeEvents,
  getGoalEvents,
  mapToMatchSmall,
  MATCH_STATUS,
  matchStatusDate,
  sortEventsMinute
} from "../../../config/utils";
import { ClavaContext, ClavaRootContext } from "../../../config/contexts";
import MatchScoreDisplay from "./MatchScoreDisplay";
import ScaleFade from "../Animated/ScaleFade";
import RoundedRectangle from "../RoundedRectangle";
import { AdPositionEnum, EventType, EventTypeEnum, MatchScreen, SportEnum, TopScorer } from "../../../client/api";
import { pathMatchView, pathTeamView, type View } from "../../../config/routes";
import MatchEvent from "./MatchEvent";
import Lineup from "../Lineup";
import MatchChanges from "./MatchChanges";
import RightPanel from "./RightPanel";
import MatchDayPanel from "./RightPanel/MatchDayPanel";
import Table from "../Table";
import StoreBanner from "../StoreBanner";
import SegmentedTabs from "../SegmentedTabs";
import { AdSlot } from "../AdSlot";
import FutureMatchIcon from "../../../assets/svg/FutureMatchIcon";
import Loading from "../Loading";

const Match: React.FC<
  ConnectedProps<typeof connector> & { currentView?: View }
> = ({
       match,
       thisMatchId,
       fullMatch,
       currentView,
       screenFromStore,
       loadMatchScreen,
       isLoading
     }) => {
  const [topScorer, setTopScorer] = useState<TopScorer | undefined>(undefined);
  const [screen, setScreen] = useState<MatchScreen | null>(null);
  const [status, setStatus] = useState<MATCH_STATUS>("past");
  const { l } = useContext(ClavaContext);
  const { theme } = useContext(ClavaRootContext);
  const view = currentView;

  useEffect(() => {
    if (thisMatchId && thisMatchId !== -1) {
      loadMatchScreen(thisMatchId);
    } else {
      setScreen(null);
      setTopScorer(undefined);
    }
  }, [thisMatchId, loadMatchScreen]);

  useEffect(() => {
    if (screenFromStore) {
      setScreen(screenFromStore);
      setTopScorer(screenFromStore.topScorer);
    }
  }, [screenFromStore]);
  useEffect(() => {
    const leagueMatch = screen?.leagueMatch ?? fullMatch;
    const base = match ?? (leagueMatch ? mapToMatchSmall(leagueMatch, leagueMatch.league) : undefined);
    let interval: number | undefined;
    if (base) {
      setStatus(matchStatusDate(base.startTime, base.fullMatchLength, base.endTime));
      interval = window.setInterval(() => {
        setStatus(matchStatusDate(base.startTime, base.fullMatchLength, base.endTime));
      }, 5000);
    } else {
      setStatus("past");
    }
    return () => {
      if (interval !== undefined) {
        clearInterval(interval);
      }
    };
  }, [screen, fullMatch, match]);

  const filteredEvents = useMemo((): EventType[] => {
    const base = screen?.leagueMatch ?? fullMatch;
    if (!base) return [];
    return base.events
      .filter(
        (e): e is EventType =>
          e.type === EventTypeEnum.GOAL ||
          e.type === EventTypeEnum.CHANCE ||
          e.type === EventTypeEnum.CARD
      )
      .sort(sortEventsMinute);
  }, [fullMatch, screen]);
  const live = typeof status === "number";

  const leagueMatch = screen?.leagueMatch ?? fullMatch;
  const renderSmall = match ?? (leagueMatch ? mapToMatchSmall(leagueMatch, leagueMatch.league) : undefined);
  const nowMs = Date.now();
  const statusImmediate: MATCH_STATUS = renderSmall
    ? nowMs > renderSmall.endTime
      ? "past"
      : nowMs < renderSmall.startTime
        ? "future"
        : status
    : status;
  const isFutureMatch = statusImmediate === "future";
  const futureIconVariant = theme === "dark" ? "dark" : "light";

  if (isLoading && !renderSmall) {
    return (
      <TabletPageDefault>
        <TabletContainer>
          <div className="flex items-center justify-center min-h-[200px]">
            <Loading />
          </div>
        </TabletContainer>
      </TabletPageDefault>
    );
  }

  if (!renderSmall || !renderSmall.team1 || !renderSmall.team2) {
    return (
      <div role="alert" className="p-4 text-font dark:text-font-dark">
        {translate("noMatch", l)}
      </div>
    );
  }
  return (
    <TabletPageDefault>
      <TabletContainer>
        <TwoColumnGrid className="xl:max-w-6xl">
          <MainColumn>
            <RoundedRectangle
              className="w-full pt-12 pb-4 bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark rounded-xl flex-shrink-0 relative"
              hasBorder
              hasTitle={false}
            >
              <BackButton />
              <div className="grid grid-cols-3 items-start justify-items-center w-full h-full">
                <div className="flex flex-col items-center justify-start px-2">
                  <NavLink
                    to={pathTeamView(renderSmall.team1.id, "table")}
                    className="cursor-pointer"
                  >
                    <ScaleFade isOpen hover>
                      <div className="w-12 h-12 flex items-center justify-center bg-transparent">
                        <img
                          src={renderSmall.team1.emblemUrl}
                          alt={showTranslated(renderSmall.team1.name, l)}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </ScaleFade>
                  </NavLink>
                  <NavLink
                    to={pathTeamView(renderSmall.team1.id, "table")}
                    className="cursor-pointer font-medium text-font dark:text-font-dark text-sm text-center mt-2"
                  >
                    {showTranslated(renderSmall.team1.name, l)}
                  </NavLink>
                </div>
                <div className="flex flex-col items-center justify-center h-full">
                  {renderSmall.cancelled ? (
                    <span className="text-red">
                    {translate("cancelledFull", l)}
                  </span>
                  ) : (
                    <>
                      {!isFutureMatch && (
                        <MatchScoreDisplay
                          goal1={renderSmall.goal1}
                          className={`text-center font-semibold ${
                            live ? "text-red" : "text-font dark:text-font-dark"
                          } text-base`}
                          goal2={renderSmall.goal2}
                        />
                      )}
                      <MatchStatusDisplay
                        startDate={renderSmall.startTime}
                        endTime={renderSmall.endTime}
                        fullDate
                        sectionAmount={renderSmall.sectionAmount}
                        sectionDuration={renderSmall.sectionDuration}
                        pauseDuration={renderSmall.pauseDuration}
                        hideLive={renderSmall.sports === SportEnum.SOCCER}
                        hideMinute={renderSmall.sports === SportEnum.HOCKEY}
                        statusOverride={statusImmediate}
                        mode="matchpage"
                      />
                    </>
                  )}
                </div>
                <div className="flex flex-col items-center justify-start px-2">
                  <NavLink
                    to={pathTeamView(renderSmall.team2.id, "table")}
                    className="cursor-pointer"
                  >
                    <ScaleFade isOpen hover>
                      <div className="w-12 h-12 flex items-center justify-center bg-transparent">
                        <img
                          src={renderSmall.team2.emblemUrl}
                          alt={showTranslated(renderSmall.team2.name, l)}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </ScaleFade>
                  </NavLink>
                  <NavLink
                    to={pathTeamView(renderSmall.team2.id, "table")}
                    className="cursor-pointer font-medium text-font dark:text-font-dark text-sm text-center mt-2"
                  >
                    {showTranslated(renderSmall.team2.name, l)}
                  </NavLink>
                </div>
              </div>
            </RoundedRectangle>
            <div className="mx-auto w-full xl:mx-0">
              {leagueMatch && (
                <div className="mt-6 block xl:hidden">
                  <RightPanel
                    player1={topScorer?.topScorer1}
                    player2={topScorer?.topScorer2}
                    emblem1={leagueMatch.team1.emblem}
                    emblem2={leagueMatch.team2.emblem}
                    goals1={topScorer?.topScorer1Goals}
                    goals2={topScorer?.topScorer2Goals}
                    previewLeagueName={showTranslated(leagueMatch.league.name, l)}
                    previewLeagueId={leagueMatch.league.id}
                    previewStartTime={renderSmall.startTime}
                  />
                </div>
              )}
              {leagueMatch && (
                <>
                  <SegmentedTabs
                    className="justify-start w-full pt-3 pb-6 mt-3"
                    activeKey={view ?? "highlights"}
                    replace
                    items={[
                      {
                        key: "highlights",
                        label: translate("highlights", l),
                        to: pathMatchView(renderSmall.id, "highlights")
                      },
                      {
                        key: "lineup",
                        label: translate("lineup", l),
                        to: pathMatchView(renderSmall.id, "lineup"),
                        hidden:
                          !leagueMatch.league.hasLineup ||
                          leagueMatch.league.sports === SportEnum.HOCKEY
                      },
                      {
                        key: "table",
                        label: translate("table", l),
                        to: pathMatchView(renderSmall.id, "table")
                      },
                      {
                        key: "statistics",
                        label: translate("statistics", l),
                        to: pathMatchView(renderSmall.id, "statistics")
                      }
                    ]}
                  />
                  {(!view || view === "highlights") && (
                    <RoundedRectangle
                      className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
                      hasBorder
                      hasTitle
                      titleType="upCenter"
                      titleKey="highlights"
                    >
                      <div
                        className="relative flex flex-col items-stretch justify-start mb-4 pt-8 px-8 xl:pt-6 xl:px-6">
                        {filteredEvents.map((event, index) => (
                          <MatchEvent
                            event={event}
                            team1Id={leagueMatch.team1.id}
                            key={`match-event-${event.id}`}
                            allEvents={filteredEvents}
                            currentEventIndex={index}
                            sports={leagueMatch.league.sports}
                          />
                        ))}
                        {isFutureMatch && (
                          <div className="flex flex-col items-center justify-center gap-6 py-8">
                            <FutureMatchIcon
                              className="w-40 h-40 max-w-full"
                              variant={futureIconVariant}
                              aria-hidden
                            />
                            <span className="text-center text-font-600 dark:text-font-600-dark">
                              {translate("matchNotStarted", l)}
                            </span>
                          </div>
                        )}
                        {filteredEvents.length === 0 && !isFutureMatch && (
                          <span className="w-full h-24 flex items-center justify-center text-center text-font-600 dark:text-font-600-dark">
                            {translate("noEventsEntered", l)}
                          </span>
                        )}
                        {filteredEvents.length > 0 && !isFutureMatch && (
                          <RoundedRectangle
                            className="mx-auto mt-2 flex flex-col items-center justify-between rounded px-4 py-2 bg-primary-50 dark:bg-primary-50-dark w-full flex-shrink-0"
                            hasTitle={false}
                            hasBorder={false}
                          >
                          <span className="font-medium text-center text-primary text-xs">
                            {translate("highlightsScoreTitle", l)}
                          </span>
                            <span className="font-semibold text-font dark:text-font-dark text-center text-sm">
                            {renderSmall.goal1} - {renderSmall.goal2}
                          </span>
                          </RoundedRectangle>
                        )}
                      </div>
                    </RoundedRectangle>
                  )}
                  {leagueMatch.league.hasLineup &&
                    leagueMatch.league.sports !== SportEnum.HOCKEY && (
                    <div className={view === "lineup" ? "" : "hidden"}>
                      <RoundedRectangle
                        className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
                        hasBorder
                        hasTitle
                        titleType="upCenter"
                        titleKey="lineup"
                      >
                        <Lineup
                          matchId={leagueMatch.id}
                          team1Id={leagueMatch.team1.id}
                          team2Id={leagueMatch.team2.id}
                          team1={leagueMatch.team1}
                          team2={leagueMatch.team2}
                          lineup1={leagueMatch.lineupTeam1 ?? undefined}
                          lineup2={leagueMatch.lineupTeam2 ?? undefined}
                          cardEvents={getCardEvents(leagueMatch.events)}
                          changeEvents={getChangeEvents(leagueMatch.events)}
                          goalEvents={getGoalEvents(leagueMatch.events)}
                          playerRatings={screenFromStore?.playerRating}
                        />
                      </RoundedRectangle>
                    </div>
                  )}
                  {leagueMatch.league.hasLineup &&
                    leagueMatch.league.sports !== SportEnum.HOCKEY &&
                    view === "lineup" && (
                    <div className="mb-4">
                      <AdSlot
                        type={AdPositionEnum.MATCH_LINEUP}
                        className="w-full"
                      />
                    </div>
                  )}
                  <div className={view === "table" ? "" : "hidden"}>
                    <div className="rounded-xl mb-4  w-full">
                      <Table
                        team2Id={leagueMatch.team2.id}
                        team1Id={leagueMatch.team1.id}
                        leagueId={leagueMatch.league.id}
                      />
                    </div>
                  </div>
                  <div className={view === "statistics" ? "" : "hidden"}>
                    <RoundedRectangle
                      className="rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark mb-4  w-full"
                      hasBorder
                      hasTitle
                      titleType="upCenter"
                      titleKey="statistics"
                    >
                      <StoreBanner border={false} message={translate("downloadUnlimited", l)} />
                    </RoundedRectangle>
                  </div>
                  {!isFutureMatch && (!view || view === "highlights" || view === "lineup") && (
                    <div className="w-full">
                      <MatchChanges
                        events={getChangeEvents(leagueMatch.events)}
                        team1Id={leagueMatch.team1.id}
                        team2Id={leagueMatch.team2.id}
                      />
                    </div>
                  )}
                  <div className="mb-4">
                    <AdSlot
                      type={AdPositionEnum.MATCH_SCREEN_BANNER}
                      className="w-full"
                    />
                  </div>
                  {leagueMatch && (
                    <div className="mt-6 xl:hidden">
                      <MatchDayPanel
                        title={`${leagueMatch.matchDay}. ${translate(
                          "matchDay",
                          l
                        )} - ${showTranslated(leagueMatch.league.name, l)}`}
                        leagueId={leagueMatch.league.id}
                        matchesOverride={screen?.history ?? []}
                        matchDayOverride={leagueMatch.matchDay}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </MainColumn>
          <RightRail>
            {leagueMatch && (
              <RightPanel
                player1={topScorer?.topScorer1}
                player2={topScorer?.topScorer2}
                emblem1={leagueMatch.team1.emblem}
                emblem2={leagueMatch.team2.emblem}
                goals1={topScorer?.topScorer1Goals}
                goals2={topScorer?.topScorer2Goals}
                previewLeagueName={showTranslated(leagueMatch.league.name, l)}
                previewLeagueId={leagueMatch.league.id}
                previewStartTime={renderSmall.startTime}
                betMatchId={renderSmall.id}
                betTeam1Name={showTranslated(leagueMatch.team1.name, l)}
                betTeam2Name={showTranslated(leagueMatch.team2.name, l)}
                betIsFutureMatch={isFutureMatch}
                betTeam1Emblem={leagueMatch.team1.emblem}
                betTeam2Emblem={leagueMatch.team2.emblem}
              />
            )}
            {leagueMatch && (
              <AdSlot
                type={AdPositionEnum.MATCH_RIGHT_RAIL}
                className="w-full"
                priority="adsense"
              />
            )}
            {leagueMatch && (
              <MatchDayPanel
                title={`${leagueMatch.matchDay}. ${translate(
                  "matchDay",
                  l
                )} - ${showTranslated(leagueMatch.league.name, l)}`}
                leagueId={leagueMatch.league.id}
                matchesOverride={screen?.history ?? []}
                matchDayOverride={leagueMatch.matchDay}
              />
            )}
          </RightRail>
        </TwoColumnGrid>
      </TabletContainer>
    </TabletPageDefault>
  );
};

export default connector(Match);
